/**
 * Web Audio Procedural Nature Ambience Engine
 * Generates distinct, realistic acoustic environments:
 * 1. 🌧️ Rain: Pattering raindrops, soft surface sizzle, and micro-droplet grains
 * 2. 🌲 Forest: Rustling canopy breeze, gentle ambient crickets, and lively songbird melodies
 * 3. 🌊 Stream: Distinct bubbling water ("bloop-trickle") and resonant liquid flow
 */

export type NatureSoundType = 'rain' | 'forest' | 'stream';

export class NatureAmbienceService {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isPlaying: boolean = false;
  private currentType: NatureSoundType = 'rain';

  // Active procedural loop generators
  private activeTimers: NodeJS.Timeout[] = [];
  private activeNodes: (AudioNode | { stop?: () => void; disconnect: () => void })[] = [];

  private initContext() {
    if (!this.ctx) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioCtx();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    if (!this.masterGain && this.ctx) {
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
      this.masterGain.connect(this.ctx.destination);
    }
  }

  // Create White/Pink/Brown noise buffers
  private createNoiseBuffer(type: 'pink' | 'white' | 'brown'): AudioBuffer {
    if (!this.ctx) throw new Error('No AudioContext');
    const bufferSize = this.ctx.sampleRate * 3;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);

    if (type === 'white') {
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.2;
      }
    } else if (type === 'pink') {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.05;
        b6 = white * 0.115926;
      }
    } else if (type === 'brown') {
      let lastOut = 0.0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        data[i] = (lastOut + 0.02 * white) / 1.02;
        lastOut = data[i];
        data[i] *= 0.35;
      }
    }
    return buffer;
  }

  public start(type: NatureSoundType = 'rain', targetVolume: number = 0.28) {
    this.initContext();
    if (!this.ctx || !this.masterGain) return;

    if (this.isPlaying) {
      this.clearCurrentAudio();
    }

    this.currentType = type;
    this.isPlaying = true;

    const now = this.ctx.currentTime;

    if (type === 'rain') {
      this.buildRainSoundscape();
    } else if (type === 'forest') {
      this.buildForestSoundscape();
    } else if (type === 'stream') {
      this.buildStreamSoundscape();
    }

    // Smooth gentle volume fade-in
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value || 0.001, now);
    this.masterGain.gain.exponentialRampToValueAtTime(Math.max(0.01, targetVolume), now + 1.2);
  }

  // ==========================================
  // 1. 🌧️ RAIN SOUNDSCAPE (Real Patter & Raindrop Grains)
  // ==========================================
  private buildRainSoundscape() {
    if (!this.ctx || !this.masterGain) return;

    // A. Rain Surface Mist / Gentle Downpour Texture
    const rainNoise = this.ctx.createBufferSource();
    rainNoise.buffer = this.createNoiseBuffer('pink');
    rainNoise.loop = true;

    const rainHp = this.ctx.createBiquadFilter();
    rainHp.type = 'highpass';
    rainHp.frequency.setValueAtTime(800, this.ctx.currentTime);

    const rainLp = this.ctx.createBiquadFilter();
    rainLp.type = 'lowpass';
    rainLp.frequency.setValueAtTime(4200, this.ctx.currentTime);

    const rainGain = this.ctx.createGain();
    rainGain.gain.setValueAtTime(0.35, this.ctx.currentTime);

    rainNoise.connect(rainHp);
    rainHp.connect(rainLp);
    rainLp.connect(rainGain);
    rainGain.connect(this.masterGain);
    rainNoise.start();

    this.activeNodes.push(rainNoise, rainHp, rainLp, rainGain);

    // B. Frequent Raindrop Patter Engine (Individual crisp micro-droplet grains)
    const triggerRainGrain = () => {
      if (!this.isPlaying || !this.ctx || !this.masterGain || this.currentType !== 'rain') return;
      const t = this.ctx.currentTime;

      // Filtered noise grain for authentic droplet patter impact
      const grainOsc = this.ctx.createOscillator();
      const grainGain = this.ctx.createGain();
      const grainFilter = this.ctx.createBiquadFilter();

      const freq = 1400 + Math.random() * 2600;
      grainOsc.type = 'sine';
      grainOsc.frequency.setValueAtTime(freq, t);
      grainOsc.frequency.exponentialRampToValueAtTime(freq * 0.45, t + 0.035);

      grainFilter.type = 'bandpass';
      grainFilter.frequency.setValueAtTime(freq, t);
      grainFilter.Q.setValueAtTime(2.5, t);

      const vol = 0.04 + Math.random() * 0.05;
      grainGain.gain.setValueAtTime(vol, t);
      grainGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);

      grainOsc.connect(grainFilter);
      grainFilter.connect(grainGain);
      grainGain.connect(this.masterGain);

      grainOsc.start(t);
      grainOsc.stop(t + 0.045);

      // Schedule next raindrop quickly (80ms - 220ms) for a continuous rhythmic patter
      const nextDelay = 70 + Math.random() * 150;
      const timer = setTimeout(triggerRainGrain, nextDelay);
      this.activeTimers.push(timer);
    };

    triggerRainGrain();
    // Second staggered rain grain layer for rich density
    const subTimer = setTimeout(triggerRainGrain, 50);
    this.activeTimers.push(subTimer);
  }

  // ==========================================
  // 2. 🌲 FOREST SOUNDSCAPE (Organic Leaf Rustle & Sweet Bird Chirps)
  // ==========================================
  private buildForestSoundscape() {
    if (!this.ctx || !this.masterGain) return;

    // A. Very Soft Background Forest Air (Quiet, static, no howling sweep)
    const forestAir = this.ctx.createBufferSource();
    forestAir.buffer = this.createNoiseBuffer('pink');
    forestAir.loop = true;

    const airLp = this.ctx.createBiquadFilter();
    airLp.type = 'lowpass';
    airLp.frequency.setValueAtTime(320, this.ctx.currentTime);

    const airGain = this.ctx.createGain();
    airGain.gain.setValueAtTime(0.08, this.ctx.currentTime); // very low so it doesn't overpower

    forestAir.connect(airLp);
    airLp.connect(airGain);
    airGain.connect(this.masterGain);
    forestAir.start();

    this.activeNodes.push(forestAir, airLp, airGain);

    // B. Organic Leaf Rustle Engine (Randomized flutter micro-bursts simulating rustling foliage)
    const triggerLeafRustle = () => {
      if (!this.isPlaying || !this.ctx || !this.masterGain || this.currentType !== 'forest') return;
      const t = this.ctx.currentTime;

      // Burst of filtered noise shaped like dry leaves brushing together
      const rustleSource = this.ctx.createBufferSource();
      rustleSource.buffer = this.createNoiseBuffer('pink');

      const rustleBp = this.ctx.createBiquadFilter();
      rustleBp.type = 'bandpass';
      const centerFreq = 1600 + Math.random() * 1200; // 1.6kHz - 2.8kHz crisp leaf texture
      rustleBp.frequency.setValueAtTime(centerFreq, t);
      rustleBp.Q.setValueAtTime(1.5, t);

      const rustleGain = this.ctx.createGain();
      const burstDuration = 0.2 + Math.random() * 0.45; // 200ms - 650ms soft rustle
      const peakVol = 0.035 + Math.random() * 0.04;

      rustleGain.gain.setValueAtTime(0.0001, t);
      rustleGain.gain.linearRampToValueAtTime(peakVol, t + burstDuration * 0.3);
      rustleGain.gain.exponentialRampToValueAtTime(0.0001, t + burstDuration);

      rustleSource.connect(rustleBp);
      rustleBp.connect(rustleGain);
      rustleGain.connect(this.masterGain);

      rustleSource.start(t);
      rustleSource.stop(t + burstDuration + 0.05);

      // Schedule next rustle every 250ms - 800ms for continuous natural leaf movement
      const nextRustleDelay = 220 + Math.random() * 550;
      const rustleTimer = setTimeout(triggerLeafRustle, nextRustleDelay);
      this.activeTimers.push(rustleTimer);
    };

    triggerLeafRustle();
    const secondaryRustleTimer = setTimeout(triggerLeafRustle, 180);
    this.activeTimers.push(secondaryRustleTimer);

    // C. Sweet, Natural Woodland Bird Chirps (Musical, soft-attack sine melodies)
    const playSongbird = () => {
      if (!this.isPlaying || !this.ctx || !this.masterGain || this.currentType !== 'forest') return;
      const t = this.ctx.currentTime;
      const motif = Math.floor(Math.random() * 4);

      if (motif === 0) {
        // Sweet double chirp: quick upward tweet
        this.synthesizeSoftBirdCall(2000, 2400, 0.09, t);
        this.synthesizeSoftBirdCall(2300, 2700, 0.11, t + 0.13);
      } else if (motif === 1) {
        // Musical Robin warble: 3-note melody
        this.synthesizeSoftBirdCall(2400, 2100, 0.12, t);
        this.synthesizeSoftBirdCall(2100, 2600, 0.10, t + 0.15);
        this.synthesizeSoftBirdCall(2600, 2300, 0.14, t + 0.28);
      } else if (motif === 2) {
        // Soft single cheerful chirp
        this.synthesizeSoftBirdCall(1900, 2500, 0.12, t);
      } else {
        // Gentle woodland trill (two quick notes)
        this.synthesizeSoftBirdCall(2500, 2800, 0.07, t);
        this.synthesizeSoftBirdCall(2700, 2900, 0.08, t + 0.10);
      }

      // Schedule next bird song every 1.5s - 3.2s
      const nextBirdDelay = 1500 + Math.random() * 1800;
      const birdTimer = setTimeout(playSongbird, nextBirdDelay);
      this.activeTimers.push(birdTimer);
    };

    const initialBirdTimer = setTimeout(playSongbird, 600);
    this.activeTimers.push(initialBirdTimer);
  }

  // Helper for pure, gentle sine bird chirps with soft bell envelopes (no harsh beeps)
  private synthesizeSoftBirdCall(
    startFreq: number,
    endFreq: number,
    duration: number,
    startTime: number
  ) {
    if (!this.ctx || !this.masterGain) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, startTime);
    // Smooth natural pitch inflection
    osc.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration * 0.7);
    osc.frequency.linearRampToValueAtTime(endFreq * 0.95, startTime + duration);

    // Soft bell-shaped volume envelope so it sounds musical and organic
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.linearRampToValueAtTime(0.028, startTime + duration * 0.25);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  }

  // ==========================================
  // 3. 🌊 STREAM SOUNDSCAPE (Authentic Babbling Brook Bubbles & Swirls)
  // ==========================================
  private buildStreamSoundscape() {
    if (!this.ctx || !this.masterGain) return;

    // A. Liquid Flow Resonance (Dual undulating resonant filters)
    const streamNoise = this.ctx.createBufferSource();
    streamNoise.buffer = this.createNoiseBuffer('pink');
    streamNoise.loop = true;

    const waterBp = this.ctx.createBiquadFilter();
    waterBp.type = 'bandpass';
    waterBp.frequency.setValueAtTime(650, this.ctx.currentTime);
    waterBp.Q.setValueAtTime(3.5, this.ctx.currentTime); // resonant hollow water tone

    const waterLfo = this.ctx.createOscillator();
    const waterLfoGain = this.ctx.createGain();
    waterLfo.frequency.setValueAtTime(0.4, this.ctx.currentTime); // water swirl modulation
    waterLfoGain.gain.setValueAtTime(280, this.ctx.currentTime);
    waterLfo.connect(waterLfoGain);
    waterLfoGain.connect(waterBp.frequency);
    waterLfo.start();

    const waterGain = this.ctx.createGain();
    waterGain.gain.setValueAtTime(0.24, this.ctx.currentTime);

    streamNoise.connect(waterBp);
    waterBp.connect(waterGain);
    waterGain.connect(this.masterGain);
    streamNoise.start();

    this.activeNodes.push(streamNoise, waterBp, waterLfo, waterLfoGain, waterGain);

    // B. Authentic Water Bubble Generator ("bloop-glug-trickle" upward swept sine bursts)
    const triggerWaterBubble = () => {
      if (!this.isPlaying || !this.ctx || !this.masterGain || this.currentType !== 'stream') return;
      const t = this.ctx.currentTime;

      const bubbleOsc = this.ctx.createOscillator();
      const bubbleGain = this.ctx.createGain();

      // Classic fluid bubble physics: resonant upward frequency sweep
      const baseFreq = 380 + Math.random() * 450;
      const endFreq = baseFreq + 180 + Math.random() * 220;
      const duration = 0.045 + Math.random() * 0.035;

      bubbleOsc.type = 'sine';
      bubbleOsc.frequency.setValueAtTime(baseFreq, t);
      bubbleOsc.frequency.exponentialRampToValueAtTime(endFreq, t + duration);

      const vol = 0.05 + Math.random() * 0.045;
      bubbleGain.gain.setValueAtTime(0.001, t);
      bubbleGain.gain.linearRampToValueAtTime(vol, t + duration * 0.2);
      bubbleGain.gain.exponentialRampToValueAtTime(0.0001, t + duration);

      bubbleOsc.connect(bubbleGain);
      bubbleGain.connect(this.masterGain);

      bubbleOsc.start(t);
      bubbleOsc.stop(t + duration + 0.01);

      // Schedule frequent bubble ripples (90ms - 260ms)
      const nextDelay = 80 + Math.random() * 160;
      const timer = setTimeout(triggerWaterBubble, nextDelay);
      this.activeTimers.push(timer);
    };

    triggerWaterBubble();
    const secondaryBubbleTimer = setTimeout(triggerWaterBubble, 60);
    this.activeTimers.push(secondaryBubbleTimer);
  }

  public setType(type: NatureSoundType) {
    if (this.currentType === type && this.isPlaying) return;
    if (this.isPlaying) {
      this.start(type);
    } else {
      this.currentType = type;
    }
  }

  private clearCurrentAudio() {
    // Clear all pending procedural timers
    this.activeTimers.forEach(t => clearTimeout(t));
    this.activeTimers = [];

    // Stop and disconnect all active nodes
    this.activeNodes.forEach(node => {
      try {
        if ('stop' in node && typeof node.stop === 'function') {
          node.stop();
        }
        node.disconnect();
      } catch (e) {
        // ignore cleanup errors
      }
    });
    this.activeNodes = [];
  }

  public stop() {
    this.clearCurrentAudio();

    if (this.ctx && this.masterGain && this.isPlaying) {
      const now = this.ctx.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
      this.masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    }

    this.isPlaying = false;
  }

  public getIsPlaying(): boolean {
    return this.isPlaying;
  }
}

export const natureAmbience = new NatureAmbienceService();
